import {
  Form_FormQuestionMsg,
  Form_FormSequenceAnswerMsg,
  Form_FormSequenceAnswerRelativePosition,
  Form_FormSequenceQuestionMsg,
  Form_FormStatus,
  Input_Form_FormValueMsg,
  useActivateFormRevisionMutation,
  useAnswerFormSequenceQuestionMutation,
  useFindFormSequenceAnswerMutation,
  useFindSiblingFormSequenceAnswerMutation,
  useGetAppointmentFormReferenceBlacklistQuery,
  useGetFormSequenceAnswerLazyQuery,
  useUpdateFormStatusMutation,
} from '@/generated/graphql';
import { FormNavigation } from '@components/form/form-navigation';
import { QuestionHeader } from '@components/form/question-header';
import { QuestionFactory } from '@components/form/questions/question-factory';
import { AveliosSpinner } from '@components/loading/avelios-spinner';
import { notification, Space } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

interface FormProps {
  clinicId: string;
  formRevisionGroupId: string;

  // Only set the office hour id during the booking process
  officeHourId?: string;

  onComplete: (isBlacklisted: boolean | undefined) => void;
  // Use this callback to define what should happen on "back" on the first question
  onReturn: () => void;

  /** Optional parameters when form is managed by a page with url parameters **/
  sequenceAnswerId?: string;
  onFindSiblingFormSequenceAnswer?: (id: string) => void;
  onAnswerFormSequenceQuestion?: (id: string) => void;
}

export const Form: FC<FormProps> = ({
  formRevisionGroupId,
  clinicId,
  officeHourId,
  sequenceAnswerId,
  onComplete,
  onReturn,

  onFindSiblingFormSequenceAnswer,
  onAnswerFormSequenceQuestion,
}) => {
  const { formatMessage } = useIntl();

  const [sequenceAnswer, setSequenceAnswer] = useState<Form_FormSequenceAnswerMsg | undefined>(
    undefined,
  );
  const [sequenceQuestion, setSequenceQuestion] = useState<
    Form_FormSequenceQuestionMsg | undefined
  >(undefined);

  const [pageTransitioning, setPageTransitioning] = useState<boolean>(false);

  const { data: blacklistedReferenceData, loading: blackListLoading } =
    useGetAppointmentFormReferenceBlacklistQuery({
      skip: !officeHourId,
      variables: { clinicId, officeHourId: officeHourId! },
    });
  const blackListedReference =
    blacklistedReferenceData?.getAppointmentFormReferenceBlacklist?.formReferenceBlacklist;
  const isBlacklisted: boolean | undefined = blackListedReference
    ? sequenceQuestion?.linkedReferences?.map((ref) => ref!.id!).includes(blackListedReference)
    : false;

  const [updateFormStatus, { loading: updateFormStatusLoading }] = useUpdateFormStatusMutation();
  const [activateFormRevision, { loading: activateFormRevisionLoading }] =
    useActivateFormRevisionMutation();

  const handleSuccess = () => {
    activateFormRevision({
      variables: {
        input: {
          clinicId,
          request: {
            formRevisionGroupId,
          },
        },
      },
    })?.then(({ errors }) => {
      if (errors?.length) {
        errors?.map((e) => console.error(e));
        notification.error({
          message: formatMessage({ id: 'form.error' }),
        });
      } else {
        updateFormStatus({
          variables: {
            clinicId,
            formRevisionGroupId,
            status: Form_FormStatus.FormStatusCompleted,
          },
        }).then((result) => {
          if (!result?.errors) {
            onComplete(isBlacklisted);
          }
        });
      }
    });
  };

  const [question, setQuestion] = useState<Form_FormQuestionMsg | undefined | null>(null);

  // used to fetch the latest answered question for the current form
  const [
    findFormSequenceAnswerMutation,
    { data: findFormSequenceAnswerData, loading: findFormSequenceAnswerLoading },
  ] = useFindFormSequenceAnswerMutation();
  useEffect(() => {
    if (findFormSequenceAnswerData === undefined) {
      return;
    }

    setSequenceAnswer(
      findFormSequenceAnswerData?.findFormSequenceAnswer?.response?.sequenceAnswer ?? undefined,
    );
    setSequenceQuestion(
      findFormSequenceAnswerData?.findFormSequenceAnswer?.response?.sequenceQuestion ?? undefined,
    );
    setQuestion(
      findFormSequenceAnswerData?.findFormSequenceAnswer?.response?.question ?? undefined,
    );
  }, [findFormSequenceAnswerData]);

  const [
    getFormSequenceAnswerQuery,
    { data: getFormSequenceAnswerData, loading: getFormSequenceAnswerLoading },
  ] = useGetFormSequenceAnswerLazyQuery({});
  useEffect(() => {
    if (getFormSequenceAnswerData === undefined) {
      return;
    }

    setSequenceAnswer(
      getFormSequenceAnswerData.getFormSequenceAnswer?.response?.sequenceAnswer ?? undefined,
    );
    setSequenceQuestion(
      getFormSequenceAnswerData?.getFormSequenceAnswer?.response?.sequenceQuestion ?? undefined,
    );
    setQuestion(getFormSequenceAnswerData.getFormSequenceAnswer?.response?.question ?? undefined);
  }, [getFormSequenceAnswerData]);

  useEffect(() => {
    if (
      sequenceAnswer !== undefined ||
      clinicId === undefined ||
      formRevisionGroupId === undefined
    ) {
      return;
    }

    if (sequenceAnswerId) {
      getFormSequenceAnswerQuery({
        variables: {
          clinicId,
          request: {
            sequenceAnswerId,
          },
        },
      });
    } else {
      findFormSequenceAnswerMutation({
        variables: {
          clinicId,
          request: {
            formRevisionGroupId,
            relative:
              Form_FormSequenceAnswerRelativePosition.FormSequenceAnswerRelativePositionLast,
          },
        },
      });
    }
  }, [clinicId, formRevisionGroupId, sequenceAnswerId, sequenceAnswer]);

  const [findSiblingFormSequenceAnswerMutation, { loading: findSiblingFormSequenceAnswerLoading }] =
    useFindSiblingFormSequenceAnswerMutation({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const siblingSequenceAnswer =
          data?.findSiblingFormSequenceAnswer?.response?.siblingSequenceAnswer;
        if (!siblingSequenceAnswer) {
          handleSuccess();
          return;
        }

        setSequenceAnswer(siblingSequenceAnswer);
        setSequenceQuestion(
          data?.findSiblingFormSequenceAnswer?.response?.siblingSequenceQuestion ?? undefined,
        );
        setQuestion(data?.findSiblingFormSequenceAnswer?.response?.siblingQuestion || undefined);

        if (onFindSiblingFormSequenceAnswer && siblingSequenceAnswer?.id) {
          onFindSiblingFormSequenceAnswer(siblingSequenceAnswer?.id);
        }
      },
    });

  const [answerFormSequenceQuestionMutation] = useAnswerFormSequenceQuestionMutation();
  const answerFormSequenceQuestion = useCallback(
    (
      newValue: Input_Form_FormValueMsg | undefined,
      doAutoMoveToNext: boolean,
      sequenceAnswerId: string,
    ) => {
      answerFormSequenceQuestionMutation({
        variables: {
          clinicId,
          request: {
            doAutoMoveToNext,
            sequenceAnswerId: sequenceAnswerId,
            value: newValue,
          },
        },
      }).then((response) => {
        if (!response.data) {
          return;
        }

        if (doAutoMoveToNext) {
          const nextSequenceAnswer =
            response.data.answerFormSequenceQuestion?.response?.nextSequenceAnswer;
          if (!nextSequenceAnswer) {
            handleSuccess();
            return;
          }

          setSequenceAnswer(nextSequenceAnswer ?? undefined);
          setSequenceQuestion(
            response?.data?.answerFormSequenceQuestion?.response?.nextSequenceQuestion ?? undefined,
          );
          setQuestion(
            response?.data?.answerFormSequenceQuestion?.response?.nextQuestion ?? undefined,
          );

          if (nextSequenceAnswer?.id && onAnswerFormSequenceQuestion) {
            onAnswerFormSequenceQuestion(nextSequenceAnswer.id);
          }
        } else {
          const sequenceAnswer = response.data.answerFormSequenceQuestion?.response?.sequenceAnswer;
          setSequenceAnswer(sequenceAnswer ?? undefined);
        }
      });
    },
    [clinicId, answerFormSequenceQuestionMutation],
  );

  const isFirst = !sequenceAnswer?.hasBackwardsSibling;
  const loading =
    findFormSequenceAnswerLoading ||
    getFormSequenceAnswerLoading ||
    findSiblingFormSequenceAnswerLoading ||
    updateFormStatusLoading ||
    activateFormRevisionLoading ||
    blackListLoading;

  function findSiblingFormSequenceAnswer(forwards: boolean) {
    findSiblingFormSequenceAnswerMutation({
      variables: {
        clinicId,
        request: {
          forwards,
          sequenceAnswerId: sequenceAnswer?.id,
        },
      },
    });
  }

  function nextQuestion() {
    findSiblingFormSequenceAnswer(true);
  }

  function previousQuestion() {
    if (isFirst) {
      onReturn();
    } else {
      findSiblingFormSequenceAnswer(false);
    }
  }

  return (
    <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
      <QuestionHeader question={question} />

      <AveliosSpinner spinning={loading}>
        <QuestionFactory
          key={question?.id}
          answer={sequenceAnswer}
          clinicId={clinicId}
          formRevisionGroupId={formRevisionGroupId}
          question={question}
          answerFormQuestion={answerFormSequenceQuestion}
        />
      </AveliosSpinner>
      <FormNavigation
        loading={loading}
        disabled={pageTransitioning} // Prevents the user from clicking the submit button multiple times
        isBlacklisted={isBlacklisted || false}
        isFirst={isFirst}
        next={nextQuestion}
        previous={previousQuestion}
        forwardsState={sequenceAnswer?.forwardsState}
      />
    </Space>
  );
};
